var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { get, set } from 'idb-keyval';
import { retrocycle, decycle } from './cycle.js';
const KEY = 'trackedQueries';
// Dead-letter store for permanently-rejected mutations — see
// moveTrackedQueryToFailed(). Additive: a separate idb-keyval key, no schema
// version bump, no interaction with the adoption/migration logic below.
const FAILED_KEY = 'trackedQueries-failed';
const SCHEMA_VERSION = '4';
const SCHEMA_VERSION_KEY = 'trackedQueries-schema-version';
// Set when the schema-version migration adopts a pre-existing queue that has
// no owner yet; cleared once adoptUnownedTrackedQueries() has stamped it.
// See "Retention is the hard constraint" in plan 004.
const NEEDS_ADOPTION_KEY = 'trackedQueries-needs-adoption';
const getStore = async () => JSON.parse((await get(KEY)) || '{}');
const getFailedStore = async () => JSON.parse((await get(FAILED_KEY)) || '{}');
export const getTrackedQueries = async () => {
    const store = await getStore();
    return Object.values(store).map(retrocycle);
};
/** Whether an entry with this id is on the main queue right now. */
export const isTrackedQueryQueued = async (id) => {
    const store = await getStore();
    return store[id] !== undefined;
};
export const getTrackedQueriesForOwner = async (owner) => {
    const all = await getTrackedQueries();
    return all.filter((tracked) => tracked.owner === owner);
};
/**
 * Counts entries that would NOT be replayed for `owner` right now: entries
 * with no owner yet (pre-adoption or pre-stamp) and entries owned by someone
 * else. Used to surface held work — see OfflineMutationLoader.
 */
export const countTrackedQueriesNotOwnedBy = async (owner) => {
    const all = await getTrackedQueries();
    return all.filter((tracked) => tracked.owner === undefined || tracked.owner !== owner).length;
};
// Serialises all read-modify-write operations on the store so concurrent calls
// cannot clobber each other's writes.  Each enqueued operation waits for the
// previous one to settle (resolve or reject) before reading/writing IDB.
let queueTail = Promise.resolve();
const withStoreLock = (fn) => {
    const run = queueTail.then(fn, fn);
    queueTail = run.catch(() => undefined);
    return run;
};
export const addTrackedQuery = (query) => withStoreLock(async () => {
    const store = await getStore();
    await set(KEY, JSON.stringify(Object.assign(Object.assign({}, store), { [query.context.id]: decycle(query) })));
});
export const removeTrackedQuery = (id) => withStoreLock(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _a = await getStore(), _b = id, _removed = _a[_b], store = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
    await set(KEY, JSON.stringify(store));
});
export const clearTrackedQueries = () => withStoreLock(async () => {
    await set(KEY, '{}');
});
// In-memory listeners for the failed-queries store. Not persisted — each tab
// re-reads storage on mount via getFailedTrackedQueriesForOwner(); this set
// only notifies live listeners in the current tab after a move/discard.
const failedListeners = new Set();
/**
 * Subscribes to changes in the failed-queries store (a move-to-failed or a
 * discard). Returns an unsubscribe function.
 */
export const subscribeFailedTrackedQueries = (listener) => {
    failedListeners.add(listener);
    return () => {
        failedListeners.delete(listener);
    };
};
const notifyFailedListeners = () => {
    for (const listener of failedListeners)
        listener();
};
export const getFailedTrackedQueries = async () => {
    const store = await getFailedStore();
    return Object.values(store).map(retrocycle);
};
export const getFailedTrackedQueriesForOwner = async (owner) => {
    const all = await getFailedTrackedQueries();
    return all.filter((tracked) => tracked.owner === owner);
};
/**
 * Moves a tracked query from the main queue to the dead-letter store because
 * the server permanently rejected it (see replayErrors.ts). Runs as ONE
 * store-lock pass: the failed store is written FIRST, then the entry is
 * removed from the main store. If the process crashes between those two
 * writes, the entry is duplicated in both stores rather than lost — the next
 * replay attempt will fail again on the same permanent rejection and re-move
 * it (idempotent by id), which is the safe direction to fail in.
 */
export const moveTrackedQueryToFailed = (tracked, message) => withStoreLock(async () => {
    var _a;
    const id = tracked.context.id;
    const failedStore = await getFailedStore();
    const store = await getStore();
    // `tracked` is a snapshot taken before the server answered; the entry may
    // have been re-owned since (reassignTrackedQueriesOwner), so the owner
    // stored now wins over the snapshot's.
    const stored = (_a = store[id]) !== null && _a !== void 0 ? _a : failedStore[id];
    const owner = stored === undefined
        ? tracked.owner
        : retrocycle(stored).owner;
    const failedEntry = Object.assign(Object.assign({}, tracked), { owner, failure: { message, failedAt: new Date().toISOString() } });
    await set(FAILED_KEY, JSON.stringify(Object.assign(Object.assign({}, failedStore), { [id]: decycle(failedEntry) })));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _b = store, _c = id, _removed = _b[_c], nextStore = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
    await set(KEY, JSON.stringify(nextStore));
}).then(notifyFailedListeners);
export const discardFailedTrackedQuery = (id) => withStoreLock(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _a = await getFailedStore(), _b = id, _removed = _a[_b], store = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
    await set(FAILED_KEY, JSON.stringify(store));
}).then(notifyFailedListeners);
/**
 * Moves a dead-lettered entry back onto the main queue so the next replay
 * picks it up again ("Reintentar"). Runs as ONE store-lock pass, in the
 * OPPOSITE order of moveTrackedQueryToFailed(): the entry is written to the
 * main store FIRST, then removed from the failed store. If the process
 * crashes between those two writes, the entry is duplicated in both stores
 * rather than lost — a duplicate simply replays, and if it fails again it
 * re-moves to the failed store by the same id (idempotent). No-op if `id` is
 * not present in the failed store.
 *
 * `context.id` is a uuid string (see useOfflineMutation.ts) — not
 * integer-like — so deleting any existing entry for it before spreading it
 * back in puts it at the END of the main store's key order, same as a fresh
 * addTrackedQuery(). The `failure` field is stripped since it is no longer a
 * failed entry.
 */
export const requeueFailedTrackedQuery = (id) => withStoreLock(async () => {
    const failedStore = await getFailedStore();
    const rawFailedEntry = failedStore[id];
    if (rawFailedEntry === undefined)
        return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _a = retrocycle(rawFailedEntry), { failure: _failure } = _a, tracked = __rest(_a, ["failure"]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _b = await getStore(), _c = id, _existing = _b[_c], store = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
    await set(KEY, JSON.stringify(Object.assign(Object.assign({}, store), { [id]: decycle(tracked) })));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _d = failedStore, _e = id, _removed = _d[_e], nextFailedStore = __rest(_d, [typeof _e === "symbol" ? _e : _e + ""]);
    await set(FAILED_KEY, JSON.stringify(nextFailedStore));
}).then(notifyFailedListeners);
// In-memory bus that lets any caller (e.g. the "Reintentar" button on a
// failed entry) ask every mounted OfflineMutationLoader to re-run its replay
// drain right away, instead of waiting for the next mount/owner-change
// trigger. Not persisted — only listeners in the current tab are notified.
const replayRequestListeners = new Set();
/** Subscribes to replay requests. Returns an unsubscribe function. */
export const subscribeReplayRequests = (listener) => {
    replayRequestListeners.add(listener);
    return () => {
        replayRequestListeners.delete(listener);
    };
};
/** Asks every mounted OfflineMutationLoader to re-run its replay drain. */
export const requestReplay = () => {
    for (const listener of replayRequestListeners)
        listener();
};
const updateSchema = () => {
    const currentVersion = window.localStorage.getItem(SCHEMA_VERSION_KEY);
    if (currentVersion === SCHEMA_VERSION)
        return;
    const isFreshInstall = currentVersion === null;
    window.localStorage.setItem(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
    if (isFreshInstall) {
        // Nothing was ever queued under an older schema — nothing to adopt.
        return;
    }
    // Migrating from schema '3' (or below) to '4': those entries have no
    // `owner` field. This migration used to call clearTrackedQueries() here on
    // ANY version mismatch, which would silently delete every pending parte on
    // upgrade — queued mutations are partes that exist nowhere else, so that is
    // not an acceptable simplification (see "Retention is the hard constraint"
    // in plan 004). Existing entries are left in place, unowned, and a marker
    // is set so the frontend adopts them exactly once, under whoever is signed
    // in at that point — see adoptUnownedTrackedQueries().
    window.localStorage.removeItem('trackedQueries');
    window.localStorage.setItem(NEEDS_ADOPTION_KEY, '1');
};
updateSchema();
/**
 * One-time adoption of pre-BIL-4928 entries that have no owner: stamps
 * `owner` onto every such entry and returns how many it stamped. Runs only
 * when the schema migration left the adoption marker set (see
 * updateSchema()) and clears the marker atomically with the stamp, inside the
 * same store-lock pass, so a concurrent call cannot double-adopt.
 *
 * Rationale: on a device with pending offline work, the person signed in at
 * upgrade time is overwhelmingly the person who queued it, and attributing
 * to them is strictly safer than today's behavior (replay under whoever
 * happens to be signed in next). This is a one-time heuristic for the
 * upgrade boundary, not an ongoing rule — after this, every entry is stamped
 * at enqueue time (see OfflineLink.ts).
 */
export const adoptUnownedTrackedQueries = (owner) => withStoreLock(async () => {
    if (window.localStorage.getItem(NEEDS_ADOPTION_KEY) !== '1')
        return 0;
    const store = await getStore();
    let adoptedCount = 0;
    const next = {};
    for (const [id, rawEntry] of Object.entries(store)) {
        const tracked = retrocycle(rawEntry);
        if (tracked.owner === undefined) {
            tracked.owner = owner;
            adoptedCount++;
        }
        next[id] = decycle(tracked);
    }
    await set(KEY, JSON.stringify(next));
    window.localStorage.removeItem(NEEDS_ADOPTION_KEY);
    return adoptedCount;
});
/**
 * Re-stamps every entry owned by `from` as owned by `to` and returns how many
 * it re-stamped. For when an identity stops existing but the person behind it
 * goes on under another one (merging a managed user into the person's own
 * user): replay only reads the signed-in identity's entries, so without this
 * the old identity's queue would be held forever.
 */
export const reassignTrackedQueriesOwner = (from, to) => withStoreLock(async () => {
    const store = await getStore();
    let reassignedCount = 0;
    const next = {};
    for (const [id, rawEntry] of Object.entries(store)) {
        const tracked = retrocycle(rawEntry);
        if (tracked.owner === from) {
            tracked.owner = to;
            reassignedCount++;
        }
        next[id] = decycle(tracked);
    }
    await set(KEY, JSON.stringify(next));
    // Failed entries are shown per owner too, so they follow the same identity.
    const failedStore = await getFailedStore();
    let failedReassignedCount = 0;
    const nextFailed = {};
    for (const [id, rawEntry] of Object.entries(failedStore)) {
        const failed = retrocycle(rawEntry);
        if (failed.owner === from) {
            failed.owner = to;
            failedReassignedCount++;
        }
        nextFailed[id] = decycle(failed);
    }
    if (failedReassignedCount > 0) {
        await set(FAILED_KEY, JSON.stringify(nextFailed));
    }
    return { reassignedCount, failedReassignedCount };
}).then(({ reassignedCount, failedReassignedCount }) => {
    if (failedReassignedCount > 0)
        notifyFailedListeners();
    return reassignedCount + failedReassignedCount;
});
